import { audienceAvailabilitySchema } from './shopify-audience-availability.js';
/** Read-only builder proposals. Declarative schemas must remain fingerprintable on both Workers. */
import * as z from 'zod';
import { studyBuilderEvaluationGuidanceSchema } from './study-builder-guidance.js';
import { builderEditSchema, audienceSourcesSchema, builderBlockSchema, builderSectionHeaderSchema, } from './shopify-study-builder-blocks.js';
import { studyBuilderSnapshotSchema, studyBuilderWireSnapshotSchema } from './shopify-study-builder-snapshot.js';
import { studyAuthoringStateSchema } from './shopify-study-authoring-operation.js';
const identity = z.strictObject({
    visit: z.string().min(1),
    sequence: z.number().int().nonnegative(),
});
/**
 * The full variant list of the chosen product, projected server-side from the stored product pick for the version section.
 * Internal to the Worker — never on the evaluate wire. Each entry carries what a version tile draws: its option values, its
 * server-formatted price, its own picture (null: none) and whether Shopify still sells it.
 */
export const builderVariantListingSchema = z.strictObject({
    productId: z.string().min(1),
    variants: z.array(z.strictObject({
        id: z.string().min(1),
        title: z.string(),
        selectedOptions: z.array(z.strictObject({ name: z.string(), value: z.string() })),
        priceLabel: z.string().min(1),
        imageUrl: z.string().nullable(),
        availableForSale: z.boolean(),
    })),
});
/** Transport batch limit shared by request validation and the browser's unacknowledged-edit queue. */
export const studyBuilderEvaluationMaxEdits = 50;
export const shopifyStudyBuilderEvaluationRequestSchema = z.strictObject({
    identity,
    draftId: z.string().min(1),
    /** Present after the first Save; draftId identifies only the unsaved creation intent. */
    studyId: z.string().min(1).optional(),
    setupId: z.string().min(1).nullable(),
    kind: z.literal('product').nullable(),
    audienceAvailability: audienceAvailabilitySchema.nullable().optional(),
    /** Echoed from the previous response so edits do not re-read the sources. */
    audienceSources: audienceSourcesSchema.nullable().optional(),
    /**
     * True while the app still has no guided-tour choice. The route then forces a fresh Web account check so the answer can
     * carry `guidance`; once the app has received a `guidance` field (even null) it sends this false and a valid account pass
     * stands in for the check. The TestFeed account itself travels in the `X-TestFeed-Account-Pass` header, not the body.
     */
    wantsGuidance: z.boolean().optional(),
    /** Edits not yet acknowledged, in order; the server applies them to `proposed`. */
    edits: z.array(builderEditSchema).max(studyBuilderEvaluationMaxEdits).optional(),
    before: studyBuilderSnapshotSchema,
    proposed: studyBuilderSnapshotSchema,
    confirmFingerprint: z.string().nullable(),
});
/**
 * The finished lines the summary sidebar draws, built by the Worker; facts of an inactive section are left out (masked to
 * their "unavailable"/"not chosen" line). The sidebar is a pure list renderer: it computes no label, count, or currency of
 * its own. Adding a summary fact is a Worker change here, never a frontend one.
 */
export const studyBuilderSummarySchema = z.strictObject({
    title: z.string(),
    /** The product card: title, picture, finished price line and finished variant line (null when there is only one). */
    product: z
        .strictObject({
        title: z.string(),
        imageUrl: z.string().nullable(),
        priceLabel: z.string().nullable(),
        variantLine: z.string().nullable(),
    })
        .nullable(),
    /** What is being tested: the comparison badges, and finished version / changing-fields / question lines. */
    testing: z.strictObject({
        badges: z.array(z.strictObject({ label: z.string().min(1), tone: z.enum(['info', 'success', 'warning', 'critical']) })),
        versionLine: z.string().min(1),
        changingFieldsLine: z.string().nullable(),
        questionsLine: z.string().nullable(),
    }),
    /** Who the test asks: the lead audience line, then the rest already joined; "not chosen" is a finished line, not empty. */
    audience: z.strictObject({ primary: z.string().min(1), secondary: z.string().nullable() }),
    /** Shoppers and credits: the finished shopper line, plus the estimate the credits bar needs (null when unknown). */
    shoppers: z.strictObject({
        line: z.string().min(1),
        creditEstimate: z.number().int().nonnegative().nullable(),
    }),
});
const readinessLineSchema = z.strictObject({ sectionId: z.string().min(1), text: z.string().min(1) });
/** A heading plus one help sentence the action bar shows beside Submit for an in-flight browser state. */
const readinessWordsSchema = z.strictObject({ heading: z.string().min(1), help: z.string().min(1) });
/**
 * Whether the test can be submitted, and the finished words beside Submit.
 * `enabled` is the Worker's verdict: true when current authoring permits preparation, no required field is missing and no problem exists. The app lowers
 * it only for an in-flight browser fact (a check running, a proposal awaiting confirm, a source read that failed); it
 * re-derives nothing. `problems` is something wrong (an age range the wrong way round, a comparison warning, too few
 * candidates), each tied to its section; a required field left blank is NOT a problem (its field carries `requiredReason`).
 * `submit` is the base words the Worker already chose from `enabled`: when enabled, a heading + help; when blocked, one
 * help sentence ("Fix the problems above…" or "Fill in the fields marked * to submit.") and no heading. `whileChecking`
 * and `onReadFailure` are the words for the two browser-only states; the app swaps to one of them by the in-flight fact it
 * alone knows, choosing among nothing else.
 */
export const studyBuilderReadinessSchema = z
    .strictObject({
    enabled: z.boolean(),
    problems: z.array(readinessLineSchema),
    submit: z.strictObject({ heading: z.string().min(1).nullable(), help: z.string().min(1) }),
    whileChecking: readinessWordsSchema,
    onReadFailure: readinessWordsSchema,
})
    .refine((readiness) => !(readiness.enabled && readiness.problems.length > 0), {
    message: 'STUDY_BUILDER_READINESS_INVALID: enabled must be false while a problem exists.',
});
/** One section of the evaluation response. Every section is drawn by the generic frame from its header and blocks. */
export const studyBuilderSectionSchema = z
    .strictObject({
    id: z.string(),
    guidance: z
        .strictObject({
        title: z.string(),
        /** The finished guide text for this section: the Worker already joined the inactive sentence when the section is
         * inactive, so the app shows it verbatim and decides nothing. */
        text: z.string(),
    })
        .optional(),
    header: builderSectionHeaderSchema.nullable(),
    /** What the merchant edits. */
    body: z.array(builderBlockSchema),
    /** Live results from the Worker: errors, warnings, counts, and the buttons that act on them. */
    status: z.array(builderBlockSchema),
    /** Small print, drawn as a footnote; a section that waits for a product also says so here, once. */
    footer: z.array(builderBlockSchema),
})
    .superRefine((section, ctx) => {
    // A section waiting on the product may be only its header (dimmed, no blocks yet); it must still have that header.
    if (section.header === null)
        ctx.addIssue({
            code: 'custom',
            message: 'STUDY_BUILDER_SECTION_INVALID: Every section must carry a header.',
        });
});
export const shopifyStudyBuilderEvaluationResponseSchema = z.strictObject({
    identity,
    kind: z.literal('product').nullable(),
    fingerprint: z.string().min(1),
    requiresConfirmation: z.boolean(),
    changeDescriptions: z.array(z.string()),
    resetDescriptions: z.array(z.string()),
    snapshot: studyBuilderWireSnapshotSchema,
    /** Same scoped status read that supplied Submit permission; refreshes the existing status query without another read. */
    authoringState: studyAuthoringStateSchema.nullable(),
    sections: z.array(studyBuilderSectionSchema),
    /** The sources behind the audience sections, for the app to echo back. */
    audienceSources: audienceSourcesSchema.nullable().optional(),
    /** Which guided tour to offer (null: none) and whether it can run now (`available`). Sent only by an answer that checked
     * the account with Web; the app keeps it. */
    guidance: studyBuilderEvaluationGuidanceSchema.nullable().optional(),
    audienceAvailability: audienceAvailabilitySchema.nullable().optional(),
    /** Whether the test can be submitted, and why not: the problems (each tied to a section) and one sentence in `words.blocked`. */
    readiness: studyBuilderReadinessSchema,
    /** The alert at the very top, for a problem no drawn field or section can show (a refused edit that no field, block or section owns). Null: none. */
    pageAlert: z.strictObject({ text: z.string().min(1) }).nullable(),
    /** The facts the summary sidebar's client-rendered pieces draw from (product, testing, audience, shoppers, title). */
    summary: studyBuilderSummarySchema,
    /** The summary sidebar itself as a section: its heading, the dividers, the sub-section headings and the client-rendered
     * pieces (`client` blocks placed by kind), drawn by the one `SectionBlocks`/`SectionFrame` renderer like every section. */
    summarySection: studyBuilderSectionSchema,
    /** Milliseconds after which the app should re-evaluate on its own because authoring or a response source is still being
     * prepared. Null: nothing is pending and the app does not poll. One generic mechanism:
     * the app only re-sends the same request after this delay; it never polls per section or per feature. */
    retryAfterMs: z.number().int().nonnegative().nullable(),
    /** Edits the Worker could not apply, one entry each (`reason` is for logs; `sectionId` names the section whose module refused it, null when no one section owns the key). Every other edit was applied; the app drops these from its waiting list and the Worker answers each once, on its field, in its section or in the page alert. */
    rejectedEdits: z.array(z.strictObject({ key: z.string().min(1), reason: z.string(), sectionId: z.string().min(1).nullable() })),
});
export const SHOPIFY_STUDY_BUILDER_EVALUATE_OPERATION = {
    id: 'study-builder.evaluate',
    method: 'POST',
    path: '/api/study-builder/evaluate',
    requestSchema: shopifyStudyBuilderEvaluationRequestSchema,
    responseSchema: shopifyStudyBuilderEvaluationResponseSchema,
    auth: {
        merchant: true,
        operator: true,
        requiresOfflineToken: true,
        merchantOnlyGates: [],
    },
    effects: [
        {
            kind: 'external-fetch',
            description: 'Signed direct-study authoring status read for scoped product authority and submission permission',
        },
        {
            kind: 'external-fetch',
            description: 'Signed Web population availability read; checked current publication and eligible candidate count',
        },
        {
            kind: 'external-fetch',
            description: 'Live Shopify product/variants/custom fields once when the chosen product changes (the product pick)',
        },
        {
            kind: 'd1-write',
            description: 'Stores the product pick (product, variants, custom fields) in D1 when the chosen product changes',
        },
    ],
    // Writes the product-pick cache on a product change, so it is not dry-run-capable (there is no skip-write dry-run path).
    // The write is an idempotent upsert keyed by the test id, so re-running the operation is still safe (`idempotent: true`).
    risk: { cost: 'free', destructive: false, dryRunCapable: false },
    idempotent: true,
};
//# sourceMappingURL=shopify-study-builder-evaluation-operation.js.map